import {
  Box,
  Button,
  Checkbox,
  CheckboxGroup,
  Flex,
  HStack,
  Input,
  Radio,
  RadioGroup,
  Stack,
  Text,
  Textarea,
  VStack,
  useToast,
  Badge,
  Alert,
  AlertIcon,
  AlertTitle,
  AlertDescription,
} from "@chakra-ui/react"
import { format, roundToNearestMinutes } from "date-fns"
import { useEffect, useState } from "react"
import { useDispatch, useSelector } from "react-redux"
import { useLocation, useNavigate } from "react-router-dom"
import { formatMobile } from "../global-client"
import { getTemplates } from "../redux/actions/smsActions"
import axios from "axios"

const BulkSendSMSScreen = () => {
  const dispatch = useDispatch()
  const { templates } = useSelector((state) => state.smsTemplate)
  const [selectedTemplate, setSelectedTemplate] = useState(null)
  const [smsPreview, setSmsPreview] = useState("")
  const navigate = useNavigate()
  const location = useLocation()
  const { jobs } = location.state || { jobs: [] }
  const [delaySelected, setDelaySelected] = useState("1")
  const [smsDelayMinutes, setSmsDelayMinutes] = useState(30)
  const [smsScheduleTime, setSmsScheduleTime] = useState(
    roundToNearestMinutes(new Date(), { roundingMethod: "ceil", nearestTo: 30 })
  )
  const [completeFlag, setCompleteFlag] = useState(false)
  const [pickedupFlag, setPickedupFlag] = useState(false)
  const [irreparableFlag, setIrreparableFlag] = useState(false)
  const [removeFlag, setRemoveFlag] = useState(false)
  const [confirmFlag, setConfirmFlag] = useState(false)
  const [unconfirmFlag, setUnconfirmFlag] = useState(false)
  const [cancelFlag, setCancelFlag] = useState(false)
  const [sending, setSending] = useState(false)

  const toast = useToast()

  const handleInputChange = (e) => {
    setSmsPreview(e.target.value)
  }

  useEffect(() => {
    dispatch(getTemplates())
  }, [dispatch])

  useEffect(() => {
    if (!jobs || jobs.length === 0) {
      navigate("/jobs")
    }
  }, [jobs, navigate])

  const handleTemplateSelect = (template) => {
    setSelectedTemplate(template)
    setCompleteFlag(template.AC_COMPLETE === 1)
    setPickedupFlag(template.AC_PICKEDUP === 1)
    setIrreparableFlag(template.AC_IRREPARABLE === 1)
    setRemoveFlag(template.AC_REMOVE === 1)
    setConfirmFlag(template.AC_CONFIRM === 1)
    setUnconfirmFlag(template.AC_UNCONFIRM === 1)
    setCancelFlag(template.AC_CANCEL === 1)
    // For bulk SMS, use a generic preview with placeholders
    setSmsPreview(template.SAVEDSMSMESS_TEXT || "")
  }

  const handleBulkSendSMS = async () => {
    setSending(true)
    
    let timeToSend = new Date()
    if (delaySelected === "3") {
      timeToSend = smsScheduleTime
    } else if (delaySelected === "2") {
      timeToSend = new Date(Date.now() + Number(smsDelayMinutes) * 60000)
    }

    const bulkSmsData = {
      jobs: jobs.map((job) => ({
        jobId: job.JOB_ID,
        jobNo: job.JOB_NO,
        custId: job.CUSTOMER_ID,
        custName: job.CUST_FNAME + " " + job.CUST_SNAME,
        toNumber: job.CUST_CONT1,
        fName: job.CUST_FNAME,
        sName: job.CUST_SNAME,
        itemName: job.CONSOLE_NAME || job.JOB_NAME2 || job.JOB_NAME1,
        total: job.TOTAL,
        balance: job.BALANCE,
        eta: job.ETA,
      })),
      messageTemplate: smsPreview,
      from: "+61407103182",
      delayOption: delaySelected,
      delayMinutes: smsDelayMinutes,
      scheduledTime: timeToSend,
      flags: {
        complete: completeFlag,
        pickedup: pickedupFlag,
        irreparable: irreparableFlag,
        remove: removeFlag,
        confirm: confirmFlag,
        unconfirm: unconfirmFlag,
        cancel: cancelFlag,
      },
    }

    try {
      const { data } = await axios.post("/api/sms/send-bulk", bulkSmsData)
      
      toast({
        title: "Bulk SMS Sent",
        description: `Successfully sent ${data.successCount} SMS messages to ${jobs.length} customers.`,
        status: "success",
        duration: 5000,
        isClosable: true,
      })

      navigate("/jobs")
    } catch (error) {
      console.error("Error sending bulk SMS:", error)
      toast({
        title: "Error",
        description: error.response?.data?.message || "Failed to send bulk SMS. Please try again.",
        status: "error",
        duration: 5000,
        isClosable: true,
      })
      setSending(false)
    }
  }

  const jobsWithValidMobile = jobs.filter((job) => job.CUST_CONT1 && job.CUST_CONT1.trim() !== "")

  return (
    <>
      <Flex direction={{ base: "column", md: "row" }} mx={{ base: 2, md: 8 }} my={8} gap={8}>
        {/* Templates List */}
        <VStack
          boxShadow='xl'
          w={{ base: "100%", md: "32%" }}
          maxH={{ base: "50vh", md: "80vh" }}
          spacing={2}
          align='stretch'
        >
          <Box
            bg='gray.200'
            p={5}
            borderTopRadius='lg'
            w='100%'
            display='flex'
            flexDirection='column'
            alignItems='center'
          >
            <Text fontSize='xl' fontWeight='bold' color='blue.700'>
              SMS Templates
            </Text>
          </Box>
          <Box bg='white' p={6} borderBottomRadius='lg' boxShadow='xl' overflowY='auto'>
            {templates && templates.length > 0 ? (
              <Stack w='100%' spacing={2}>
                <Box
                  key={"0000"}
                  p={1}
                  borderRadius='lg'
                  borderWidth={selectedTemplate && selectedTemplate.SAVEDSMSMESS_ID === "0000" ? "2px" : "1px"}
                  borderColor={
                    selectedTemplate && selectedTemplate.SAVEDSMSMESS_ID === "0000" ? "blue.400" : "gray.200"
                  }
                  bg={selectedTemplate && selectedTemplate.SAVEDSMSMESS_ID === "0000" ? "blue.50" : "gray.50"}
                  boxShadow={selectedTemplate && selectedTemplate.SAVEDSMSMESS_ID === "0000" ? "md" : "sm"}
                  cursor='pointer'
                  transition='all 0.2s'
                  _hover={{ bg: "blue.100" }}
                  onClick={() =>
                    handleTemplateSelect({
                      SAVEDSMSMESS_ID: "0000",
                      SAVEDSMSMESS_NAME: "Custom Message",
                      SAVEDSMSMESS_TEXT: "",
                    })
                  }
                >
                  <Text fontWeight='semibold' color='blue.700'>
                    Custom Message
                  </Text>
                </Box>
                {templates.map((template) => (
                  <Box
                    key={template.SAVEDSMSMESS_ID}
                    p={1}
                    borderRadius='lg'
                    borderWidth={
                      selectedTemplate && selectedTemplate.SAVEDSMSMESS_ID === template.SAVEDSMSMESS_ID ? "2px" : "1px"
                    }
                    borderColor={
                      selectedTemplate && selectedTemplate.SAVEDSMSMESS_ID === template.SAVEDSMSMESS_ID
                        ? "blue.400"
                        : "gray.200"
                    }
                    bg={
                      selectedTemplate && selectedTemplate.SAVEDSMSMESS_ID === template.SAVEDSMSMESS_ID
                        ? "blue.50"
                        : "gray.50"
                    }
                    boxShadow={
                      selectedTemplate && selectedTemplate.SAVEDSMSMESS_ID === template.SAVEDSMSMESS_ID ? "md" : "sm"
                    }
                    cursor='pointer'
                    transition='all 0.2s'
                    _hover={{ bg: "blue.100" }}
                    onClick={() => handleTemplateSelect(template)}
                  >
                    <Text fontWeight='semibold' color='blue.700'>
                      {template.SAVEDSMSMESS_NAME}
                    </Text>
                  </Box>
                ))}
              </Stack>
            ) : (
              <Text color='gray.500'>No templates available.</Text>
            )}
          </Box>
        </VStack>

        {/* Main Content */}
        <VStack w={{ base: "100%", md: "68%" }} spacing={6} align='stretch'>
          {/* Recipients Card */}
          <Box bg='gray.200' p={6} borderRadius='lg' boxShadow='md' w='100%'>
            <VStack align='stretch' spacing={3}>
              <HStack spacing={4}>
                <Text fontSize='md' color='gray.600'>
                  Sending Bulk SMS to:
                </Text>
                <Badge colorScheme='blue' fontSize='md' px={3} py={1} borderRadius='full'>
                  {jobsWithValidMobile.length} Recipients
                </Badge>
              </HStack>
              {jobsWithValidMobile.length !== jobs.length && (
                <Alert status='warning' borderRadius='md'>
                  <AlertIcon />
                  <Box>
                    <AlertTitle fontSize='sm'>Some jobs excluded</AlertTitle>
                    <AlertDescription fontSize='xs'>
                      {jobs.length - jobsWithValidMobile.length} job(s) do not have valid mobile numbers and will be skipped.
                    </AlertDescription>
                  </Box>
                </Alert>
              )}
              <Box maxH='200px' overflowY='auto' bg='white' p={3} borderRadius='md'>
                <VStack align='stretch' spacing={2}>
                  {jobsWithValidMobile.map((job) => (
                    <HStack key={job.JOB_ID} justify='space-between'>
                      <Text fontSize='sm'>
                        <Badge mr={2}>{job.JOB_NO}</Badge>
                        {job.CUST_FNAME} {job.CUST_SNAME}
                      </Text>
                      <Text fontSize='sm' color='blue.600' fontWeight='medium'>
                        {formatMobile(job.CUST_CONT1)}
                      </Text>
                    </HStack>
                  ))}
                </VStack>
              </Box>
            </VStack>
          </Box>

          {/* Editable Message Card */}
          <Box bg='white' p={6} borderRadius='lg' boxShadow='md' w='100%' h='30vh'>
            <Text
              as='label'
              fontSize='md'
              fontWeight='bold'
              color='blue.700'
              onClick={() =>
                handleTemplateSelect({
                  SAVEDSMSMESS_ID: "0000",
                  SAVEDSMSMESS_NAME: "Custom Message",
                  SAVEDSMSMESS_TEXT: "",
                })
              }
            >
              SMS Preview
            </Text>
            <Text fontSize='xs' color='gray.500' mt={1}>
              Message will be personalized for each recipient
            </Text>
            {selectedTemplate ? (
              <Textarea
                value={smsPreview}
                h='80%'
                resize='vertical'
                mt={2}
                onChange={handleInputChange}
                fontSize='md'
                borderColor='blue.300'
                focusBorderColor='blue.500'
                bg='gray.50'
              />
            ) : (
              <Text
                color='gray.500'
                onClick={() =>
                  handleTemplateSelect({
                    SAVEDSMSMESS_ID: "0000",
                    SAVEDSMSMESS_NAME: "Custom Message",
                    SAVEDSMSMESS_TEXT: "",
                  })
                }
              >
                Select a template to preview its content.
              </Text>
            )}
          </Box>

          {/* Flags Card */}
          <Box bg='white' p={4} borderRadius='lg' boxShadow='md' w='100%'>
            <CheckboxGroup colorScheme='blue'>
              <HStack wrap='wrap' spacing={4}>
                <Checkbox isChecked={completeFlag} onChange={(e) => setCompleteFlag(e.target.checked)}>
                  <Text fontSize='sm'>Complete</Text>
                </Checkbox>
                <Checkbox isChecked={pickedupFlag} onChange={(e) => setPickedupFlag(e.target.checked)}>
                  <Text fontSize='sm'>Picked-Up</Text>
                </Checkbox>
                <Checkbox isChecked={irreparableFlag} onChange={(e) => setIrreparableFlag(e.target.checked)}>
                  <Text fontSize='sm'>Not Repairable</Text>
                </Checkbox>
                <Checkbox isChecked={removeFlag} onChange={(e) => setRemoveFlag(e.target.checked)}>
                  <Text fontSize='sm'>Remove</Text>
                </Checkbox>
                <Checkbox isChecked={confirmFlag} onChange={(e) => setConfirmFlag(e.target.checked)}>
                  <Text fontSize='sm'>Confirmed</Text>
                </Checkbox>
                <Checkbox isChecked={unconfirmFlag} onChange={(e) => setUnconfirmFlag(e.target.checked)}>
                  <Text fontSize='sm'>Unconfirmed</Text>
                </Checkbox>
                <Checkbox isChecked={cancelFlag} onChange={(e) => setCancelFlag(e.target.checked)}>
                  <Text fontSize='sm'>Cancel</Text>
                </Checkbox>
              </HStack>
            </CheckboxGroup>
          </Box>

          <Flex w='100%' direction={{ base: "column", md: "row" }} gap={4}>
            {/* Time delay Card */}
            <Box bg='white' p={4} borderRadius='lg' boxShadow='md' w='100%'>
              <RadioGroup onChange={setDelaySelected} value={delaySelected}>
                <Stack direction='Column' spacing={4}>
                  <Radio value='1'>Send Now (5 second delay between each)</Radio>
                  <HStack>
                    <Radio value='2'>Send in ...</Radio>
                    <Input
                      type='number'
                      min='1'
                      max='999'
                      w='60px'
                      value={smsDelayMinutes}
                      onChange={(e) => setSmsDelayMinutes(e.target.value)}
                      onFocus={() => setDelaySelected("2")}
                    />
                    <Text>minutes</Text>
                  </HStack>
                  <HStack>
                    <Radio value='3'>Send at ...</Radio>
                    <Input
                      type='datetime-local'
                      w='280px'
                      value={format(smsScheduleTime, "yyyy-MM-dd'T'HH:mm")}
                      onChange={(e) => setSmsScheduleTime(new Date(e.target.value))}
                      onFocus={() => setDelaySelected("3")}
                    />
                  </HStack>
                </Stack>
              </RadioGroup>
            </Box>

            {/* Actions Card */}
            <Box
              bg='white'
              p={6}
              borderRadius='lg'
              boxShadow='xl'
              w='100%'
              display='flex'
              justifyContent='center'
              alignItems='center'
              gap={4}
            >
              <Button
                colorScheme='blue'
                size='lg'
                onClick={() => handleBulkSendSMS()}
                isLoading={sending}
                disabled={
                  !selectedTemplate ||
                  smsPreview.trim() === "" ||
                  jobsWithValidMobile.length === 0 ||
                  (delaySelected === "3" && new Date(smsScheduleTime) < new Date())
                }
              >
                {delaySelected === "1" && "Send Bulk SMS Now"}
                {(delaySelected === "2" || delaySelected === "3") && "Schedule Bulk SMS"}
              </Button>
              <Button colorScheme='gray' size='lg' onClick={() => navigate(-1)}>
                Cancel
              </Button>
            </Box>
          </Flex>
        </VStack>
      </Flex>
    </>
  )
}

export default BulkSendSMSScreen
